import React from 'react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Checkbox,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import type {
  SampleLineScheduleDetailRow,
  UnscheduledSampleGroupOption,
} from '../../pages/SamplingSchedulesPage/samplingSchedulePlans';
import { ChemInline } from '../ChemInline';
import { ChemParameterUjiCell } from '../ChemParameterUjiCell';

type Props = {
  options: UnscheduledSampleGroupOption[];
  selectedGroupId: string | null;
  locked?: boolean;
  selectionEnabled?: boolean;
  /** Setelah group terpilih & petugas pertama disimpan, checkbox group tidak bisa diubah. */
  selectionLocked?: boolean;
  onSelectGroup: (quotationGroupDocumentId: string | null) => void;
};

const EMPTY_PU_LOOKUP = new Map<string, { nama_parameter: string | null; simbol: string | null }>();

function DetailRowValue({ row }: { row: SampleLineScheduleDetailRow }) {
  if (row.kind === 'parameters' && row.parameterItems?.length) {
    return (
      <ChemParameterUjiCell
        items={row.parameterItems}
        puLookup={EMPTY_PU_LOOKUP}
        separator=" · "
        emptyFallback="—"
      />
    );
  }
  if (row.kind === 'simbols') {
    const parts = row.value
      .split('·')
      .map((part) => part.trim())
      .filter(Boolean);
    if (!parts.length) return <>{row.value || '—'}</>;
    return (
      <Box
        component="span"
        sx={{
          display: 'inline',
          wordBreak: 'break-word',
          '& .katex-html': { whiteSpace: 'normal' },
        }}
      >
        {parts.map((part, index) => (
          <React.Fragment key={`${part}-${index}`}>
            {index > 0 ? ' · ' : null}
            <ChemInline text={part} />
          </React.Fragment>
        ))}
      </Box>
    );
  }
  return <>{row.value}</>;
}

function DetailRows({ rows }: { rows: SampleLineScheduleDetailRow[] }) {
  return (
    <Stack spacing={1}>
      {rows.map((row) => (
        <Box key={row.label}>
          <Typography variant="caption" color="text.secondary" display="block">
            {row.label}
          </Typography>
          <Typography
            variant="body2"
            component="div"
            sx={{ '& .katex': { fontSize: '0.95em' } }}
          >
            <DetailRowValue row={row} />
          </Typography>
        </Box>
      ))}
    </Stack>
  );
}

export function ScheduleSampleGroupAccordionPicker({
  options,
  selectedGroupId,
  locked = false,
  selectionEnabled = true,
  selectionLocked = false,
  onSelectGroup,
}: Props) {
  const checkboxDisabled = locked || selectionLocked || !selectionEnabled;

  return (
    <Stack spacing={0.75}>
      {options.map((option) => {
        const selectionKey = option.selectionKey?.trim() || option.quotationGroupDocumentId;
        const checked = selectedGroupId === selectionKey;
        const isOtherGroup = Boolean(selectedGroupId) && !checked;
        const optionDisabled = Boolean(option.disabled);
        return (
          <Accordion
            key={selectionKey}
            disableGutters
            elevation={0}
            sx={{
              border: 1,
              borderColor: checked ? 'primary.main' : 'divider',
              borderRadius: '4px !important',
              '&:before': { display: 'none' },
              overflow: 'hidden',
              opacity: optionDisabled || (isOtherGroup && selectionLocked) ? 0.55 : 1,
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon fontSize="small" />}
              sx={{
                minHeight: 44,
                '& .MuiAccordionSummary-content': { alignItems: 'center', my: 0.5 },
              }}
            >
              <Checkbox
                size="small"
                checked={checked}
                sx={{ p: 0.5, mr: 0.5, visibility: selectionEnabled ? 'visible' : 'hidden' }}
                disabled={
                  checkboxDisabled || optionDisabled || (selectionLocked && isOtherGroup)
                }
                onClick={(e) => e.stopPropagation()}
                onChange={(e) => {
                  e.stopPropagation();
                  if (checkboxDisabled || optionDisabled) return;
                  onSelectGroup(e.target.checked ? selectionKey : null);
                }}
                inputProps={{
                  'aria-label': `Pilih group sample ${option.label}`,
                }}
                data-testid={`schedule-assign-sample-group-${selectionKey}`}
              />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: checked ? 700 : 500 }}>
                  {option.label}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block">
                  {option.sampleLines.length} sample line
                  {optionDisabled && option.disabledReason ? ` · ${option.disabledReason}` : ''}
                </Typography>
                {option.sampleLines.map((line) => (
                  <Typography
                    key={`${option.quotationGroupDocumentId}-${line.lineIndex}-summary`}
                    variant="caption"
                    color="text.secondary"
                    display="block"
                    sx={{ lineHeight: 1.35 }}
                  >
                    {line.label}
                    {' · '}
                    {line.samplePointCountLabel} titik
                    {' · '}
                    {line.hargaLabel}
                  </Typography>
                ))}
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0, pb: 1.5 }}>
              <Stack spacing={1.5}>
                {option.groupDetails.length > 0 ? (
                  <DetailRows rows={option.groupDetails} />
                ) : null}
                {option.sampleLines.map((line, index) => (
                  <Box key={`${option.quotationGroupDocumentId}-${line.lineIndex}`}>
                    {index > 0 || option.groupDetails.length > 0 ? (
                      <Divider sx={{ mb: 1.5 }} />
                    ) : null}
                    <Typography variant="subtitle2" sx={{ mb: 0.75 }}>
                      {line.label}
                    </Typography>
                    <DetailRows rows={line.details} />
                  </Box>
                ))}
              </Stack>
            </AccordionDetails>
          </Accordion>
        );
      })}
    </Stack>
  );
}

/** @deprecated Use ScheduleSampleGroupAccordionPicker */
export const ScheduleSampleLineAccordionPicker = ScheduleSampleGroupAccordionPicker;
